<script setup lang="ts">
/**
 * AuroraBackground — the product's signature.
 *
 * A fixed, full-viewport field of soft light-leaks ("Nordic aurora over
 * deep water"), anchored on the brand accent via the --aurora-* tokens.
 * Sits behind all app content (pointer-events: none), shows through the
 * translucent surfaces, and is purely decorative (aria-hidden).
 *
 * Static and unfiltered since 2026-09-27. Four half-screen blobs under
 * `filter: blur(72px)`, drifting forever, made the GPU re-blur them — and
 * every frosted panel above them — on every frame: scrolling lagged on
 * laptops with integrated graphics. The radial gradients already fade to
 * transparent, so the blur added little but cost; the drift was too slow to
 * notice.
 */
</script>

<template>
  <div class="aurora" aria-hidden="true">
    <span class="aurora__blob aurora__blob--1"></span>
    <span class="aurora__blob aurora__blob--2"></span>
    <span class="aurora__blob aurora__blob--3"></span>
    <span class="aurora__blob aurora__blob--4"></span>
    <span class="aurora__grain"></span>
  </div>
</template>

<style scoped>
.aurora {
  position: fixed;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  /* Slightly deepen the cream base so the light-leaks have something to
     glow against without darkening the reading surfaces above. */
  background: radial-gradient(
    120% 80% at 50% -10%,
    color-mix(in srgb, var(--color-accent) 6%, transparent),
    transparent 60%
  );
}
.aurora__blob {
  position: absolute;
  border-radius: 50%;
  opacity: var(--aurora-opacity);
}
/* Top-left — the navy anchor, large + low. */
.aurora__blob--1 {
  width: 52vw;
  height: 52vw;
  top: -16vw;
  left: -10vw;
  background: radial-gradient(
    circle at 50% 50%,
    var(--aurora-1),
    transparent 72%
  );
}
/* Right — the aurora teal-green sweep. */
.aurora__blob--2 {
  width: 46vw;
  height: 46vw;
  top: -8vw;
  right: -14vw;
  background: radial-gradient(
    circle at 50% 50%,
    var(--aurora-2),
    transparent 72%
  );
}
/* Lower-left — indigo depth. */
.aurora__blob--3 {
  width: 44vw;
  height: 44vw;
  bottom: -18vw;
  left: 4vw;
  background: radial-gradient(
    circle at 50% 50%,
    var(--aurora-3),
    transparent 72%
  );
}
/* Lower-right horizon — the pale gold glow, the warm note. */
.aurora__blob--4 {
  width: 40vw;
  height: 40vw;
  bottom: -20vw;
  right: -6vw;
  background: radial-gradient(
    circle at 50% 50%,
    var(--aurora-4),
    transparent 74%
  );
  opacity: calc(var(--aurora-opacity) + 0.06);
}
/* A whisper of grain breaks up the gradient banding so the blur reads as
   light, not as a flat color wash. Tiny + cheap; no motion. */
.aurora__grain {
  position: absolute;
  inset: 0;
  opacity: 0.025;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
</style>
